import { useState, useEffect, useRef, useCallback, useMemo } from 'react'

// Status enum mapping to match backend IntegrationStatus
const getStatusNameFromValue = (statusValue: number): string => {
  const statusMap: { [key: number]: string } = {
    0: 'credential_error',
    1: 'active', 
    2: 'no_content',
    3: 'error',
    4: 'oauth_stub',
    5: 'verifying',
    6: 'revoked',
    7: 'pending_credentials'
  }
  return statusMap[statusValue] || ''
}
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import { useAuth } from '@/contexts/auth-context'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import { integrationsService, IntegrationType, IntegratorType, CollectionStatsType } from '@/services/integrations'
import { integratorsService } from '@/services/integrators'
import { api } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { toast } from 'sonner'
import { Plus, Search, MoreHorizontal, AlertTriangle, Loader2, Filter, RefreshCw, X } from 'lucide-react'

// Filter configuration for integrations
const FILTER_STORAGE_KEY = 'integrations-filters'
const SORT_STORAGE_KEY = 'integrations-sort'

interface IntegrationFilters {
  statuses: {
    active: boolean
    disabled: boolean
    error: boolean
    credential_error: boolean
    pending_credentials: boolean
    no_content: boolean
    oauth_stub: boolean
    verifying: boolean
    revoked: boolean
  }
  segments: Record<string, boolean>
  tags: Record<string, boolean>
}

interface IntegrationSort {
  field: string
  order: 'asc' | 'desc'
}

const DEFAULT_FILTERS: IntegrationFilters = {
  statuses: {
    active: true,
    disabled: true,
    error: true,
    credential_error: true,
    pending_credentials: true,
    no_content: true,
    oauth_stub: true,
    verifying: true,
    revoked: true,
  },
  segments: {}, // Will be populated dynamically from integrators
  tags: {}, // Will be populated dynamically from integrations
}

const DEFAULT_SORT: IntegrationSort = {
  field: 'id',
  order: 'desc',
}

// Map status keys to user-friendly labels
const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    active: 'Active',
    disabled: 'Disabled',
    error: 'Error',
    credential_error: 'Invalid Credentials',
    pending_credentials: 'Pending Credentials',
    no_content: 'No Content',
    oauth_stub: 'OAuth Stub',
    verifying: 'Verifying',
    revoked: 'Revoked',
  }
  return labels[status] || status.replace(/_/g, ' ')
}

const loadFiltersFromStorage = (): IntegrationFilters => {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    if (stored) {
      return { ...DEFAULT_FILTERS, ...JSON.parse(stored) }
    }
  } catch (e) {
    console.error('Failed to load filters from storage:', e)
  }
  return DEFAULT_FILTERS
}

const saveFiltersToStorage = (filters: IntegrationFilters) => {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
  } catch (e) {
    console.error('Failed to save filters to storage:', e)
  }
}

const loadSortFromStorage = (): IntegrationSort => {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Validate the stored values
      if (parsed.field && ['asc', 'desc'].includes(parsed.order)) {
        return parsed
      }
    }
  } catch (e) {
    console.error('Failed to load sort from storage:', e)
  }
  return DEFAULT_SORT
}

const saveSortToStorage = (sort: IntegrationSort) => {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort))
  } catch (e) {
    console.error('Failed to save sort to storage:', e)
  }
}

export function IntegrationsPage() {
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentOrganization } = useOrganization()
  const { } = useAuth()
  const { hasAdminAccess, isOwner } = useRoleAccess()
  const isAdmin = hasAdminAccess() || isOwner()

  const [integrations, setIntegrations] = useState<IntegrationType[]>([])
  const [integrators, setIntegrators] = useState<IntegratorType[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => searchParams.get('search') || '')
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [totalRecords, setTotalRecords] = useState(0)
  const [allIntegrationsCount, setAllIntegrationsCount] = useState(0) // Unfiltered total
  const pageSize = 30
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [sortField, setSortField] = useState<string>(() => loadSortFromStorage().field)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => loadSortFromStorage().order)
  const [showNewDialog, setShowNewDialog] = useState(false)
  const [selectedIntegrator, setSelectedIntegrator] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [integratorSearch, setIntegratorSearch] = useState<string>('')
  const [creatingIntegration, setCreatingIntegration] = useState(false)
  const [_authorizations, setAuthorizations] = useState<any[]>([])
  const [selectedAuthorizationId, setSelectedAuthorizationId] = useState<string>('create_new')
  const [_loadingAuthorizations, setLoadingAuthorizations] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [integrationToDelete, setIntegrationToDelete] = useState<IntegrationType | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [collectionStats, setCollectionStats] = useState<CollectionStatsType[]>([])
  const [allTags, setAllTags] = useState<string[]>([])

  // Valid status filter values that can be used in URL
  const validStatusFilters = ['active', 'disabled', 'error', 'credential_error', 'pending_credentials', 'no_content', 'oauth_stub', 'verifying', 'revoked']

  // Initialize filters - check URL param first, otherwise show all statuses
  // When navigating via sidebar (no URL param), always reset to show all integrations
  const [filters, setFilters] = useState<IntegrationFilters>(() => {
    const statusParam = new URLSearchParams(window.location.search).get('status')
    const storedFilters = loadFiltersFromStorage()

    // Inline the valid statuses check since component variables aren't available in initializer
    const validStatuses = ['active', 'disabled', 'error', 'credential_error', 'pending_credentials', 'no_content', 'oauth_stub', 'verifying', 'revoked']

    if (statusParam) {
      // URL has status param - use it for filtering
      const requestedStatuses = statusParam.split('|').filter(s => validStatuses.includes(s))
      if (requestedStatuses.length > 0) {
        const newStatuses = Object.keys(DEFAULT_FILTERS.statuses).reduce((acc, key) => {
          acc[key as keyof typeof DEFAULT_FILTERS.statuses] = requestedStatuses.includes(key)
          return acc
        }, {} as typeof DEFAULT_FILTERS.statuses)
        return { ...storedFilters, statuses: newStatuses }
      }
    }

    // No URL param - reset status filters to show all (keep other filters from storage)
    return { ...storedFilters, statuses: DEFAULT_FILTERS.statuses }
  })

  const [credentialErrorCount, setCredentialErrorCount] = useState<number>(0)

  // Update URL with status filter (called explicitly on user actions only)
  // Supports multiple statuses joined by pipe: ?status=credential_error|pending_credentials
  // Preserves existing search param
  const setUrlStatusParam = useCallback((statuses: string[]) => {
    const newParams = new URLSearchParams(searchParams)
    if (statuses.length > 0 && statuses.length < validStatusFilters.length) {
      newParams.set('status', statuses.join('|'))
    } else {
      newParams.delete('status')
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams, validStatusFilters.length])

  // Update URL with search filter (preserves existing status param)
  const setUrlSearchParam = useCallback((search: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (search.trim()) {
      newParams.set('search', search.trim())
    } else {
      newParams.delete('search')
    }
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // Sync filters when URL changes (e.g., browser back button)
  useEffect(() => {
    // Sync status param
    const statusParam = searchParams.get('status')
    if (statusParam) {
      const requestedStatuses = statusParam.split('|').filter(s => validStatusFilters.includes(s))
      if (requestedStatuses.length > 0) {
        const newStatuses = Object.keys(DEFAULT_FILTERS.statuses).reduce((acc, key) => {
          acc[key as keyof typeof DEFAULT_FILTERS.statuses] = requestedStatuses.includes(key)
          return acc
        }, {} as typeof DEFAULT_FILTERS.statuses)
        // Only update if different from current
        const currentActive = Object.entries(filters.statuses).filter(([_, v]) => v).map(([k]) => k).sort().join('|')
        const newActive = requestedStatuses.sort().join('|')
        if (currentActive !== newActive) {
          setFilters(prev => ({ ...prev, statuses: newStatuses }))
        }
      }
    }
    // Sync search param
    const searchParam = searchParams.get('search') || ''
    if (searchParam !== debouncedSearchTerm) {
      setSearchTerm(searchParam)
      setDebouncedSearchTerm(searchParam)
    }
  }, [searchParams])

  // Commit search - called on blur or Enter key
  const commitSearch = useCallback(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setDebouncedSearchTerm(searchTerm)
      setUrlSearchParam(searchTerm)
    }
  }, [searchTerm, debouncedSearchTerm, setUrlSearchParam])

  // Persist sort settings to localStorage
  useEffect(() => {
    saveSortToStorage({ field: sortField, order: sortOrder })
  }, [sortField, sortOrder])

  // Fetch credential error count and total integrations count on mount
  useEffect(() => {
    if (currentOrganization) {
      integrationsService.getCredentialErrorCount()
        .then(count => setCredentialErrorCount(count))
        .catch(() => setCredentialErrorCount(0))

      // Fetch total integrations count (unfiltered) for the badge
      integrationsService.list(1, 1)
        .then(response => setAllIntegrationsCount(response.total || 0))
        .catch(() => {})
    }
  }, [currentOrganization])

  // Load integrations and integrators
  // Also reload when single-status filter changes (server-side filtering)
  const singleStatusFilter = Object.entries(filters.statuses).filter(([_, v]) => v).length === 1
    ? Object.entries(filters.statuses).find(([_, v]) => v)?.[0]
    : null

  useEffect(() => {
    if (currentOrganization) {
      loadData()
    }
  }, [currentOrganization, sortField, sortOrder, debouncedSearchTerm, singleStatusFilter])

  // Refresh data when page becomes visible (e.g., returning from edit page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentOrganization) {
        // Silently refresh integrations without showing loading state
        integrationsService.list(
          1,
          pageSize,
          sortField,
          sortOrder,
          debouncedSearchTerm || undefined,
          getServerStatusFilter()
        ).then(response => {
          setIntegrations(response.integrations || [])
          setTotalRecords(response.total || 0)
          setCurrentPage(1)
          setHasMorePages(pageSize < (response.total || 0))
        }).catch(() => {
          // Silently fail - user can manually refresh if needed
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [currentOrganization, sortField, sortOrder, debouncedSearchTerm, pageSize])

  // Update segment filters when integrators change
  useEffect(() => {
    if (integrators.length === 0) return

    const uniqueSegments = new Set(integrators.map(i => i.segment).filter(Boolean))

    // Check if there are new segments not in current filters
    const hasNewSegments = [...uniqueSegments].some(seg => filters.segments[seg] === undefined)

    if (hasNewSegments) {
      const segmentFilters: Record<string, boolean> = { ...filters.segments }
      uniqueSegments.forEach(seg => {
        if (segmentFilters[seg] === undefined) {
          segmentFilters[seg] = true // New segments default to enabled
        }
      })
      updateFilters({ ...filters, segments: segmentFilters })
    }
  }, [integrators])

  // Update tag filters when allTags changes (loaded from API)
  useEffect(() => {
    if (allTags.length === 0) return

    // Initialize all tags as enabled
    const tagFilters: Record<string, boolean> = {}
    allTags.forEach(tag => {
      // Preserve existing filter state, default to true for new tags
      tagFilters[tag] = filters.tags[tag] ?? true
    })

    // Only update if there are changes
    const existingTagKeys = Object.keys(filters.tags).sort().join(',')
    const newTagKeys = Object.keys(tagFilters).sort().join(',')
    if (existingTagKeys !== newTagKeys) {
      updateFilters({ ...filters, tags: tagFilters })
    }
  }, [allTags])

  // Fetch authorizations when integrator is selected
  useEffect(() => {
    if (selectedIntegrator) {
      fetchAuthorizations()
    } else {
      setAuthorizations([])
      setSelectedAuthorizationId('create_new')
    }
  }, [selectedIntegrator])

  const fetchAuthorizations = async () => {
    if (!selectedIntegrator) return

    const integrator = integrators.find(i => i.slug === selectedIntegrator)
    if (!integrator || integrator.technology !== 'api_oauth2') {
      setAuthorizations([])
      return
    }

    setLoadingAuthorizations(true)
    try {
      const response = await api.get(`/oauth/authorizations?integrator_id=${integrator.id}`)
      setAuthorizations(response.data.authorizations || [])
    } catch (error) {
      console.error('Failed to fetch authorizations:', error)
      setAuthorizations([])
    } finally {
      setLoadingAuthorizations(false)
    }
  }

  // Prevent React Strict Mode from causing multiple requests
  useEffect(() => {
    return () => {
      // Cleanup function to prevent multiple executions
    }
  }, [])

  // Map status name to status code for server-side filtering
  const statusCodeMap: Record<string, number> = {
    'credential_error': 0,
    'active': 1,
    'no_content': 2,
    'error': 3,
    'oauth_stub': 4,
    'verifying': 5,
    'revoked': 6,
    'pending_credentials': 7,
  }

  // Get server-side status filter when specific statuses are selected
  const getServerStatusFilter = useCallback((): number[] | undefined => {
    const activeStatuses = Object.entries(filters.statuses).filter(([_, v]) => v).map(([k]) => k)

    // Handle single status filter
    if (activeStatuses.length === 1) {
      const statusCode = statusCodeMap[activeStatuses[0]]
      if (statusCode !== undefined) {
        return [statusCode]
      }
    }


    return undefined
  }, [filters.statuses])

  // Handle infinite scroll - load more items from server
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMorePages) return

    setLoadingMore(true)
    try {
      const nextPage = currentPage + 1
      const serverStatusFilter = getServerStatusFilter()
      const integrationsResponse = await integrationsService.list(
        nextPage,
        pageSize,
        sortField,
        sortOrder,
        debouncedSearchTerm || undefined,
        serverStatusFilter
      )

      // Append new integrations to existing list
      setIntegrations(prev => [...prev, ...integrationsResponse.integrations])
      setCurrentPage(nextPage)

      // Check if there are more pages
      const totalLoaded = nextPage * pageSize
      setHasMorePages(totalLoaded < integrationsResponse.total)
    } catch (error) {
      console.error('Failed to load more integrations:', error)
    } finally {
      setLoadingMore(false)
    }
  }, [currentPage, loadingMore, hasMorePages, sortField, sortOrder, debouncedSearchTerm, getServerStatusFilter])

  // Set up intersection observer for infinite scroll - preload when 500px from bottom
  useEffect(() => {
    if (loading || !scrollContainerRef.current) return

    const options = {
      root: scrollContainerRef.current, // Use scroll container as root
      rootMargin: '500px', // Start loading 500px before reaching the trigger
      threshold: 0.1
    }

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        handleLoadMore()
      }
    }, options)

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [loading, handleLoadMore])

  const loadData = async () => {
    if (!currentOrganization) return

    setLoading(true)
    setCurrentPage(1)
    setHasMorePages(true)
    try {
      // Load first page of integrations with optional search and status filter
      const serverStatusFilter = getServerStatusFilter()
      const integrationsResponse = await integrationsService.list(
        1,
        pageSize,
        sortField,
        sortOrder,
        debouncedSearchTerm || undefined,
        serverStatusFilter
      )

      setIntegrations(integrationsResponse.integrations || [])
      setTotalRecords(integrationsResponse.total || 0)
      setHasMorePages(pageSize < (integrationsResponse.total || 0))

      // Load integrators separately with better error handling
      // Only load on initial page load (when integrators is empty)
      if (integrators.length === 0) {
        try {
          const integratorsData = await integratorsService.list()
          // SDK-only vendors (Apps SDK catalog, technology='sdk') are shown to ADMINS only —
          // admins connect SDK-init creds there. Regular users see just reporting integrations.
          setIntegrators(isAdmin ? integratorsData : integratorsData.filter((i) => (i as { technology?: string }).technology !== 'sdk'))

          // Initialize segment filters with ALL unique segments from all integrators
          const uniqueSegments = new Set(integratorsData.map(i => i.segment).filter(Boolean))

          // Initialize segment filters - preserve existing status filters (may be set from URL)
          const segmentFilters: Record<string, boolean> = {}
          uniqueSegments.forEach(seg => {
            segmentFilters[seg] = true
          })
          updateFilters({
            ...filters,
            segments: segmentFilters
          })
        } catch (integratorsError) {
          console.error('Failed to load integrators:', integratorsError)
          setIntegrators([])
        }
      }

      // Load collection statistics
      try {
        const collectionStatsResponse = await integrationsService.getCollectionStats()
        setCollectionStats(collectionStatsResponse.collection_stats)
      } catch (statsError: any) {
        // Don't show error to user, just use empty stats
        setCollectionStats([])
      }

      // Load all tags for the organization
      try {
        const tagsResponse = await integrationsService.getAllTags()
        setAllTags(tagsResponse.tags || [])
      } catch (tagsError: any) {
        console.error('Failed to load tags:', tagsError)
        setAllTags([])
      }
    } catch (error: any) {
      console.error('Failed to load data:', error)
      
      if (error.response?.status === 401) {
        // 401 errors are now handled by auth context - just show user message
        toast.error('Session expired. Please log in again.')
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again in a few moments.')
      } else {
        toast.error('Failed to load integrations')
        setIntegrations([])
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStatusToggle = async (integration: IntegrationType) => {
    try {
      const newStatus = !integration.enabled
      const updatedIntegration = await integrationsService.setStatus(
        integration.id!,
        newStatus
      )
      
      setIntegrations(integrations.map(i => 
        i.id === integration.id ? updatedIntegration : i
      ))
      
      toast.success(`Integration ${newStatus ? 'enabled' : 'disabled'} successfully`)
    } catch (error) {
      toast.error('Failed to update integration status')
    }
  }

  const getIntegrator = (integratorId?: number) => {
    return integrators.find(integrator => String(integrator.id) === String(integratorId))
  }

  const getCollectionStats = (integrationId?: number) => {
    return collectionStats.find(stats => stats.integration_id === integrationId)
  }

  const formatLastDate = (integration: IntegrationType) => {
    const stats = getCollectionStats(integration.id)
    if (stats?.last_date) {
      // Parse as UTC date since database values are in UTC and represent data collection date
      const utcDate = new Date(stats.last_date.replace(' ', 'T') + 'Z')
      return utcDate.toISOString().split('T')[0] // YYYY-MM-DD format
    }
    if (integration.last_date) {
      const utcDate = new Date(integration.last_date.toString().replace(' ', 'T') + 'Z')
      return utcDate.toISOString().split('T')[0]
    }
    return '-'
  }

  const formatRecords = (integrationId?: number) => {
    const stats = getCollectionStats(integrationId)
    if (stats?.last_recs) {
      return stats.last_recs.toLocaleString()
    }
    return '-'
  }

  const getStatusBadge = (integration: IntegrationType) => {
    let variant: 'default' | 'destructive' | 'secondary' | 'outline' = 'default'
    let label = ''

    // If disabled, show Disabled status
    if (!integration.enabled) {
      return <Badge variant="secondary">Disabled</Badge>
    }

    // Handle both string and integer status values
    const statusValue = typeof integration.status === 'number'
      ? getStatusNameFromValue(integration.status)
      : integration.status

    // If no valid status, return empty badge
    if (!statusValue) {
      return null
    }

    // Check for process errors first - overrides status
    if (integration.last_process_status === 2) {
      return <Badge variant="destructive">Error</Badge>
    }

    switch (statusValue) {
      case 'credential_error':
        variant = 'destructive'
        label = 'Invalid Credentials'
        break
      case 'active':
        variant = 'default'
        label = 'Active'
        break
      case 'no_content':
        variant = 'secondary'
        label = 'No Content'
        break
      case 'error':
        variant = 'destructive'
        label = 'Error'
        break
      case 'pending_credentials':
        variant = 'outline'
        label = 'Pending Credentials'
        break
      case 'verifying':
        variant = 'outline'
        label = 'Verifying'
        break
      case 'oauth_stub':
        variant = 'outline'
        label = 'OAuth Pending'
        break
      case 'revoked':
        variant = 'destructive'
        label = 'Revoked'
        break
    }

    if (!label) return null

    return <Badge variant={variant}>{label}</Badge>
  }

  // Helper function to get the displayed status for an integration
  const getDisplayStatus = (integration: IntegrationType): string => {
    if (!integration.enabled) {
      return 'disabled'
    }
    if (integration.last_process_status === 2) {
      return 'error'
    }
    const statusValue = typeof integration.status === 'number'
      ? getStatusNameFromValue(integration.status)
      : integration.status
    return statusValue || 'active'
  }

  // Status sort order (lower = better/first when ascending)
  const statusSortOrder: Record<string, number> = {
    'active': 1,
    'no_content': 2,
    'verifying': 3,
    'oauth_stub': 4,
    'pending_credentials': 5,
    'disabled': 6,
    'credential_error': 7,
    'revoked': 8,
    'error': 9,
  }

  // Apply client-side status filtering and sorting for collection stats fields
  // Note: Text search for name, integrator, segment, and ID is now handled server-side
  const filteredAndSortedIntegrations = integrations.filter(integration => {
    // Determine the displayed status (same logic as badge)
    const filterStatus = getDisplayStatus(integration)

    // Check if this status is enabled in filters
    const statusKey = filterStatus as keyof typeof filters.statuses
    if (filters.statuses[statusKey] === false) {
      return false
    }

    // Check if this segment is enabled in filters
    const integrator = integrators.find(i => String(i.id) === String(integration.integrator_id))
    const segment = integrator?.segment || ''
    if (segment && filters.segments[segment] === false) {
      return false
    }

    // Check if this tag is enabled in filters
    const tag = integration.tag || ''
    // If there are tag filters and this integration has a tag, check if it's enabled
    // If integration has no tag, only filter it out if there are active tag filters and "(none)" is not enabled
    if (Object.keys(filters.tags).length > 0) {
      if (tag) {
        if (filters.tags[tag] === false) {
          return false
        }
      }
    }

    // When sorting by last_date, exclude integrations with no last_date
    if (sortField === 'last_date') {
      const stats = collectionStats.find(s => s.integration_id === integration.id)
      const hasLastDate = stats?.last_date || integration.last_date
      if (!hasLastDate) {
        return false
      }
    }

    return true
  }).sort((a, b) => {
    // Client-side sorting for fields that need computed values
    if (sortField === 'status') {
      const aStatus = getDisplayStatus(a)
      const bStatus = getDisplayStatus(b)
      const aOrder = statusSortOrder[aStatus] || 99
      const bOrder = statusSortOrder[bStatus] || 99

      if (sortOrder === 'asc') {
        return aOrder - bOrder
      } else {
        return bOrder - aOrder
      }
    }

    if (sortField === 'recs') {
      const aStats = getCollectionStats(a.id)
      const bStats = getCollectionStats(b.id)
      const aRecs = aStats?.last_recs || 0
      const bRecs = bStats?.last_recs || 0

      if (sortOrder === 'asc') {
        return aRecs - bRecs
      } else {
        return bRecs - aRecs
      }
    }

    if (sortField === 'last_date') {
      const aStats = getCollectionStats(a.id)
      const bStats = getCollectionStats(b.id)

      // Get the actual displayed date (collection stats first, then integration date)
      // Nulls are already filtered out when sorting by last_date
      const aDate = aStats?.last_date ? new Date(aStats.last_date.replace(' ', 'T') + 'Z') :
                    new Date(a.last_date!.toString().replace(' ', 'T') + 'Z')
      const bDate = bStats?.last_date ? new Date(bStats.last_date.replace(' ', 'T') + 'Z') :
                    new Date(b.last_date!.toString().replace(' ', 'T') + 'Z')

      if (sortOrder === 'asc') {
        return aDate.getTime() - bDate.getTime()
      } else {
        return bDate.getTime() - aDate.getTime()
      }
    }

    // For all other fields, sorting is handled server-side
    return 0
  })

  const handleSort = (field: string) => {
    if (field === 'last_date') {
      // 3-state sorting for last_date: asc -> desc -> no sort
      if (sortField === 'last_date') {
        if (sortOrder === 'asc') {
          setSortOrder('desc')
        } else {
          // Reset to default sort (id desc)
          setSortField('id')
          setSortOrder('desc')
        }
      } else {
        setSortField('last_date')
        setSortOrder('asc')
      }
    } else if (sortField === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field with default desc order
      setSortField(field)
      setSortOrder('desc')
    }
    // Note: loadData will be triggered by useEffect due to sortField/sortOrder dependency
  }

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  const updateFilters = (newFilters: IntegrationFilters) => {
    setFilters(newFilters)
    saveFiltersToStorage(newFilters)
    // Note: URL is updated explicitly in filter click handlers, not here
  }

  const toggleStatusFilter = (status: keyof IntegrationFilters['statuses']) => {
    const newFilters = {
      ...filters,
      statuses: {
        ...filters.statuses,
        [status]: !filters.statuses[status],
      },
    }
    updateFilters(newFilters)

    // Update URL based on new filter state
    const activeStatuses = Object.entries(newFilters.statuses).filter(([_, v]) => v).map(([k]) => k)
    setUrlStatusParam(activeStatuses)
  }

  const toggleSegmentFilter = (segment: string) => {
    const newFilters = {
      ...filters,
      segments: {
        ...filters.segments,
        [segment]: !filters.segments[segment],
      },
    }
    updateFilters(newFilters)
  }

  const clearStatusFilters = () => {
    const allDisabled = Object.keys(filters.statuses).reduce((acc, key) => {
      acc[key as keyof typeof filters.statuses] = false
      return acc
    }, {} as typeof filters.statuses)
    updateFilters({ ...filters, statuses: allDisabled })
    setUrlStatusParam([]) // Clear URL param when no statuses selected
  }

  const selectAllStatusFilters = () => {
    updateFilters({ ...filters, statuses: DEFAULT_FILTERS.statuses })
    setUrlStatusParam([]) // Clear URL param when showing all statuses
  }

  const clearSegmentFilters = () => {
    const allDisabled: Record<string, boolean> = {}
    Object.keys(filters.segments).forEach(seg => {
      allDisabled[seg] = false
    })
    updateFilters({ ...filters, segments: allDisabled })
  }

  const selectAllSegmentFilters = () => {
    const allEnabled: Record<string, boolean> = {}
    Object.keys(filters.segments).forEach(seg => {
      allEnabled[seg] = true
    })
    updateFilters({ ...filters, segments: allEnabled })
  }

  const toggleTagFilter = (tag: string) => {
    const newFilters = {
      ...filters,
      tags: {
        ...filters.tags,
        [tag]: !filters.tags[tag],
      },
    }
    updateFilters(newFilters)
  }

  const clearTagFilters = () => {
    const allDisabled: Record<string, boolean> = {}
    Object.keys(filters.tags).forEach(tag => {
      allDisabled[tag] = false
    })
    updateFilters({ ...filters, tags: allDisabled })
  }

  const selectAllTagFilters = () => {
    const allEnabled: Record<string, boolean> = {}
    Object.keys(filters.tags).forEach(tag => {
      allEnabled[tag] = true
    })
    updateFilters({ ...filters, tags: allEnabled })
  }

  // Count active filters - show count of selected items (only if not all selected)
  const selectedStatusCount = Object.values(filters.statuses).filter(v => v).length
  const totalStatuses = Object.keys(filters.statuses).length
  const showStatusCount = selectedStatusCount > 0 && selectedStatusCount < totalStatuses

  // Count selected segment filters
  const segmentEntries = Object.entries(filters.segments)
  const selectedSegmentCount = segmentEntries.filter(([_, v]) => v).length
  const totalSegments = segmentEntries.length
  const showSegmentCount = selectedSegmentCount > 0 && selectedSegmentCount < totalSegments

  // Count selected tag filters
  const tagEntries = Object.entries(filters.tags)
  const selectedTagCount = tagEntries.filter(([_, v]) => v).length
  const totalTags = tagEntries.length
  const showTagCount = selectedTagCount > 0 && selectedTagCount < totalTags

  // Refresh credential error count from API
  const refreshCredentialErrorCount = () => {
    integrationsService.getCredentialErrorCount()
      .then(count => setCredentialErrorCount(count))
      .catch(() => {})
  }

  // Check if currently filtering only credential errors (needed before filterCredentialErrors)
  const isFilteringCredentialErrors = filters.statuses.credential_error &&
    Object.entries(filters.statuses).filter(([k, v]) => v).length === 1

  // Filter to show only credential errors - triggers server-side reload
  const filterCredentialErrors = () => {
    const allDisabled = Object.keys(filters.statuses).reduce((acc, key) => {
      acc[key as keyof typeof filters.statuses] = key === 'credential_error'
      return acc
    }, {} as typeof filters.statuses)
    updateFilters({ ...filters, statuses: allDisabled })
    setUrlStatusParam(['credential_error'])
  }

  const handleCreateIntegration = async () => {
    if (!selectedIntegrator) {
      toast.error('Please select an integrator')
      return
    }

    setCreatingIntegration(true)
    try {
      const integrator = integrators.find(i => i.slug === selectedIntegrator)
      if (!integrator) {
        throw new Error('Integrator not found')
      }

      // Create the integration
      const newIntegration = {
        name: integrator.name,
        integrator_id: integrator.id,
        mapping: integrator.mapping,
        // Include authorization_id if using existing authorization
        ...(integrator.technology === 'api_oauth2' &&
            selectedAuthorizationId !== 'create_new' ?
            { authorization_id: parseInt(selectedAuthorizationId) } : {})
      }

      const response = await integrationsService.create(newIntegration)
      
      if (response.id) {
        toast.success('Integration created successfully')
        setShowNewDialog(false)
        setSelectedIntegrator('')
        setSelectedAuthorizationId('create_new')
        setAuthorizations([])
        // Refresh the integrations list and credential error count
        loadData()
        refreshCredentialErrorCount()
        
        // Always navigate to edit page for all integrator types
        // This provides a consistent flow where users can see what credentials are needed
        // and choose when to start OAuth authorization
        navigate(`/${orgSlug}/integrations/${response.id}/edit`)
      }
    } catch (error) {
      console.error('Failed to create integration:', error)
      toast.error('Failed to create integration')
    } finally {
      setCreatingIntegration(false)
    }
  }

  // Helper function to open OAuth popup window
  // @ts-ignore - Function kept for future OAuth functionality
  const _openOAuthPopup = async (integrationId: number, integratorSlug: string) => {
    const width = 562
    const height = 720
    const left = (screen.width/2)-(width/2)
    const top = (screen.height/2)-(height/2)
    
    try {
      // Get OAuth URL from parent window to avoid auth issues in popup
      const response = await api.get(`/oauth/integrations/${integrationId}/oauth_url`)

      if (response.data && response.data.oauth_url) {
        // Pass the OAuth URL directly to the popup to avoid additional API calls
        const oauthUrl = `/${orgSlug}/oauth_integration?integration_id=${integrationId}&slug=${integratorSlug}&oauth_url=${encodeURIComponent(response.data.oauth_url)}`

        window.open(
          oauthUrl,
          '_blank',
          `toolbar=no,scrollbars=no,resizable=no,top=${top},left=${left},width=${width},height=${height}`
        )
      } else {
        return
      }
    } catch (error) {
      return
    }
    
    // Listen for messages from the OAuth popup
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return
      }
      
      if (event.data.type === 'OAUTH_SUCCESS') {
        // Navigate to edit page to complete credential setup
        const integrationId = event.data.data?.integrationId || event.data.integrationId
        if (integrationId) {
          navigate(`/${orgSlug}/integrations/${integrationId}/edit`)
        } else {
          // Fallback: refresh integrations list if no integration ID
          setTimeout(() => {
            loadData()
          }, 1000)
        }

        // Don't try to close popup - it will close itself
        window.removeEventListener('message', handleMessage)
      } else if (event.data.type === 'OAUTH_ERROR') {
        // Don't try to close popup - it will close itself
        window.removeEventListener('message', handleMessage)
      }
    }
    
    // Add message listener
    window.addEventListener('message', handleMessage)
    
    // Clean up after a reasonable timeout (popup should send message or close itself)
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handleMessage)
    }, 60000) // 60 second timeout
    
    // Update handleMessage to also clean up the timeout
    const originalHandleMessage = handleMessage
    const wrappedHandleMessage = (event: MessageEvent) => {
      clearTimeout(timeout)
      window.removeEventListener('message', wrappedHandleMessage)
      originalHandleMessage(event)
    }
    
    // Replace the event listener with the wrapped version
    window.removeEventListener('message', handleMessage)
    window.addEventListener('message', wrappedHandleMessage)
  }

  const handleDelete = async () => {
    if (!integrationToDelete) return
    
    setDeleting(true)
    try {
      await integrationsService.delete(integrationToDelete.id!)
      toast.success('Integration deleted successfully')
      setDeleteDialogOpen(false)
      setIntegrationToDelete(null)
      // Refresh the integrations list and credential error count
      loadData()
      refreshCredentialErrorCount()
    } catch (error) {
      console.error('Failed to delete integration:', error)
      toast.error('Failed to delete integration')
    } finally {
      setDeleting(false)
    }
  }

  // Sort integrators alphabetically
  const sortedIntegrators = [...integrators].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  // Determine if we can load more from server
  // Search is now handled server-side, so we can always use pagination
  const canLoadMore = hasMorePages && !loadingMore && !loading && integrations.length > 0


  return (
    <TooltipProvider>
    <div className="flex flex-col h-[calc(100vh-6rem)] gap-4">
      {/* Fixed Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            Integrations
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Badge variant="secondary" className="cursor-default">
                      {allIntegrationsCount || totalRecords}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Total Integrations</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {credentialErrorCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-medium cursor-pointer transition-opacity hover:opacity-80 ${
                        isFilteringCredentialErrors
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-destructive text-destructive-foreground'
                      }`}
                      onClick={() => isFilteringCredentialErrors ? selectAllStatusFilters() : filterCredentialErrors()}
                    >
                      {credentialErrorCount}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>
                      {isFilteringCredentialErrors
                        ? 'Click to show all integrations'
                        : 'Click to filter by credential errors'
                      }
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </h2>
          <p className="text-slate-600">
            Connect and manage your data integrations
          </p>
        </div>
        <Button onClick={() => setShowNewDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New
        </Button>
      </div>

      {/* Fixed Search Bar */}
      <div className="flex items-center flex-shrink-0">
        <div className="relative w-96">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, integrator, segment, or ID... (Enter to search)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onBlur={commitSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitSearch()
              }
            }}
            className="pl-8 pr-8 w-full"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('')
                setDebouncedSearchTerm('')
                setUrlSearchParam('')
              }}
              className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Table Container - horizontal scroll for mobile/tablet */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 rounded-md border overflow-auto">
        <Table className="min-w-[1200px]">
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead
                    className="w-[60px] cursor-pointer select-none hover:bg-muted/50 bg-background"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center gap-1">
                      ID
                      {getSortIcon('id') && (
                        <span className="text-xs">{getSortIcon('id')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="w-[80px] cursor-pointer select-none hover:bg-muted/50 bg-background"
                    onClick={() => handleSort('integrator')}
                  >
                    <div className="flex items-center gap-1">
                      Integrator
                      {getSortIcon('integrator') && (
                        <span className="text-xs">{getSortIcon('integrator')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="w-[25%] text-left cursor-pointer select-none hover:bg-muted/50 bg-background"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {getSortIcon('name') && (
                        <span className="text-xs">{getSortIcon('name')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="w-[100px] bg-background">
                    <div className="flex items-center gap-1">
                      <span
                        className="cursor-pointer hover:underline select-none"
                        onClick={() => handleSort('tag')}
                      >
                        Tag
                      </span>
                      {totalTags > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-6 px-1 ${showTagCount ? 'bg-primary/10 text-primary' : ''}`}
                            >
                              <Filter className="h-3 w-3" />
                              {showTagCount && <span className="ml-1 text-xs">{selectedTagCount}</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-52 p-3" align="end">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">Filter by Tag</span>
                              {selectedTagCount > 0 ? (
                                <Button variant="ghost" size="sm" onClick={clearTagFilters} className="h-6 px-2 text-xs">
                                  Clear
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" onClick={selectAllTagFilters} className="h-6 px-2 text-xs">
                                  All
                                </Button>
                              )}
                            </div>
                            <div className="space-y-1 max-h-[300px] overflow-y-auto">
                              {Object.keys(filters.tags).sort().map((tag) => (
                                <div key={tag} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`tag-${tag}`}
                                    checked={filters.tags[tag]}
                                    onCheckedChange={() => toggleTagFilter(tag)}
                                  />
                                  <Label htmlFor={`tag-${tag}`} className="text-sm font-normal cursor-pointer">
                                    {tag}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      {getSortIcon('tag') && (
                        <span className="text-xs">{getSortIcon('tag')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="w-[120px] bg-background">
                    <div className="flex items-center gap-1">
                      <span
                        className="cursor-pointer hover:underline select-none"
                        onClick={() => handleSort('segment')}
                      >
                        Segment
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 px-1 ${showSegmentCount ? 'bg-primary/10 text-primary' : ''}`}
                          >
                            <Filter className="h-3 w-3" />
                            {showSegmentCount && <span className="ml-1 text-xs">{selectedSegmentCount}</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-52 p-3" align="end">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Filter by Segment</span>
                            {selectedSegmentCount > 0 ? (
                              <Button variant="ghost" size="sm" onClick={clearSegmentFilters} className="h-6 px-2 text-xs">
                                Clear
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={selectAllSegmentFilters} className="h-6 px-2 text-xs">
                                All
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1 max-h-[300px] overflow-y-auto">
                            {Object.keys(filters.segments).sort().map((segment) => (
                              <div key={segment} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`segment-${segment}`}
                                  checked={filters.segments[segment]}
                                  onCheckedChange={() => toggleSegmentFilter(segment)}
                                />
                                <Label htmlFor={`segment-${segment}`} className="text-sm font-normal cursor-pointer capitalize">
                                  {segment}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      {getSortIcon('segment') && (
                        <span className="text-xs">{getSortIcon('segment')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="w-[120px] cursor-pointer select-none hover:bg-muted/50 bg-background"
                    onClick={() => handleSort('last_date')}
                  >
                    <div className="flex items-center gap-1">
                      Last Updated
                      {getSortIcon('last_date') && (
                        <span className="text-xs">{getSortIcon('last_date')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="w-[100px] text-right cursor-pointer select-none hover:bg-muted/50 bg-background"
                    onClick={() => handleSort('recs')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Recs
                      {getSortIcon('recs') && (
                        <span className="text-xs">{getSortIcon('recs')}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="w-[120px] bg-background">
                    <div className="flex items-center gap-1">
                      <span
                        className="cursor-pointer hover:underline select-none"
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 px-1 ${showStatusCount ? 'bg-primary/10 text-primary' : ''}`}
                          >
                            <Filter className="h-3 w-3" />
                            {showStatusCount && <span className="ml-1 text-xs">{selectedStatusCount}</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-52 p-3" align="end">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Filter by Status</span>
                            {selectedStatusCount > 0 ? (
                              <Button variant="ghost" size="sm" onClick={clearStatusFilters} className="h-6 px-2 text-xs">
                                Clear
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={selectAllStatusFilters} className="h-6 px-2 text-xs">
                                All
                              </Button>
                            )}
                          </div>
                          <div className="space-y-1 max-h-[300px] overflow-y-auto">
                            {(Object.keys(filters.statuses) as Array<keyof IntegrationFilters['statuses']>).map((status) => (
                              <div key={status} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`status-${status}`}
                                  checked={filters.statuses[status]}
                                  onCheckedChange={() => toggleStatusFilter(status)}
                                />
                                <Label htmlFor={`status-${status}`} className="text-sm font-normal cursor-pointer">
                                  {getStatusLabel(status)}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      {getSortIcon('status') && (
                        <span className="text-xs">{getSortIcon('status')}</span>
                      )}
                    </div>
                  </TableHead>
                  {isFilteringCredentialErrors && (
                    <TableHead className="min-w-[300px] bg-background">
                      Details
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && integrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isFilteringCredentialErrors ? 9 : 8} className="text-center py-12">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Loading integrations...</p>
                    </TableCell>
                  </TableRow>
                ) : !loading && filteredAndSortedIntegrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isFilteringCredentialErrors ? 9 : 8} className="text-center py-12">
                      <p className="text-muted-foreground">No integrations found</p>
                    </TableCell>
                  </TableRow>
                ) : filteredAndSortedIntegrations.length === 0 ? null : (
                  filteredAndSortedIntegrations
                    .filter((integration, index, self) =>
                      index === self.findIndex(i => i.id === integration.id)
                    )
                    .map((integration) => {
                    const integrator = getIntegrator(integration.integrator_id)
                    const hasError = integration.last_process_status === 2 && integration.enabled
                    
                    const editUrl = `/${orgSlug}/integrations/${integration.id}/edit`

                    return (
                      <TableRow
                        key={integration.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={(e) => {
                          // Cmd/Ctrl+click opens in new tab
                          if (e.metaKey || e.ctrlKey) {
                            window.open(editUrl, '_blank', 'noopener')
                          } else {
                            navigate(editUrl)
                          }
                        }}
                        onAuxClick={(e) => {
                          // Middle-click opens in new tab
                          if (e.button === 1) {
                            e.preventDefault()
                            window.open(editUrl, '_blank', 'noopener')
                          }
                        }}
                      >
                        <TableCell>{integration.id}</TableCell>
                        <TableCell>
                          {integrator && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <img
                                  src={`https://storage.googleapis.com/integrator-guide/logos/${integrator.slug}.png`}
                                  alt={integrator.name}
                                  className="h-7 w-7 object-contain cursor-pointer"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{integrator.slug}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell className="text-left font-medium truncate">
                          <Link
                            to={editUrl}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {integration.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {integration.tag || '-'}
                        </TableCell>
                        <TableCell className="capitalize">
                          {integrator?.segment || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {formatLastDate(integration)}
                            {hasError && (
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatRecords(integration.id)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(integration)}
                        </TableCell>
                        {isFilteringCredentialErrors && (
                          <TableCell
                            className="text-sm whitespace-normal select-text cursor-text"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className={integration.last_error_message ? 'text-destructive' : 'text-muted-foreground'}>
                              {integration.last_error_message || '-'}
                            </span>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>

            {/* Infinite Scroll Trigger - inside scrollable area */}
            {canLoadMore && (
              <div
                ref={loadMoreRef}
                className="flex justify-center py-8"
              >
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}
          </div>

      {/* New Integration Dialog */}
      <Dialog open={showNewDialog} onOpenChange={(open) => {
        setShowNewDialog(open)
        if (!open) {
          setSelectedIntegrator('')
          setSelectedCategory('all')
          setIntegratorSearch('')
          setSelectedAuthorizationId('create_new')
          setAuthorizations([])
        }
      }}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle className="text-xl">Add New Integration</DialogTitle>
            <DialogDescription>
              Connect to <span className="font-semibold text-foreground">{Math.floor(integrators.length / 50) * 50}+</span> data sources
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left Panel - Categories */}
            <div className="w-48 border-r bg-muted/30 p-4 overflow-y-auto">
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All
                  <span className="float-right opacity-70">{integrators.length}</span>
                </button>
                {(() => {
                  // Get unique segments with counts
                  const segmentCounts = integrators.reduce((acc, i) => {
                    const seg = i.segment || 'Other'
                    acc[seg] = (acc[seg] || 0) + 1
                    return acc
                  }, {} as Record<string, number>)

                  return Object.entries(segmentCounts)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([segment, count]) => (
                      <button
                        key={segment}
                        onClick={() => setSelectedCategory(segment)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedCategory === segment
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {segment.charAt(0).toUpperCase() + segment.slice(1)}
                        <span className="float-right opacity-70">{count}</span>
                      </button>
                    ))
                })()}
              </div>
            </div>

            {/* Right Panel - Search & Grid */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search */}
              <div className="p-4 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search integrators..."
                    value={integratorSearch}
                    onChange={(e) => setIntegratorSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-4 gap-3">
                  {sortedIntegrators
                    .filter(i => {
                      const matchesCategory = selectedCategory === 'all' || i.segment === selectedCategory
                      const matchesSearch = !integratorSearch ||
                        i.name.toLowerCase().includes(integratorSearch.toLowerCase()) ||
                        i.slug.toLowerCase().includes(integratorSearch.toLowerCase())
                      return matchesCategory && matchesSearch
                    })
                    .map((integrator) => (
                      <button
                        key={integrator.slug}
                        onClick={() => setSelectedIntegrator(integrator.slug)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                          selectedIntegrator === integrator.slug
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-transparent bg-muted/50 hover:border-muted-foreground/20'
                        }`}
                      >
                        <div className="h-10 w-10 flex items-center justify-center">
                          <img
                            src={`https://storage.googleapis.com/integrator-guide/logos/${integrator.slug}.png`}
                            alt={integrator.name}
                            className="max-h-10 max-w-10 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              target.parentElement!.innerHTML = `<div class="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground">${integrator.name.charAt(0)}</div>`
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium text-center line-clamp-2">{integrator.name}</span>
                      </button>
                    ))}
                </div>
                {sortedIntegrators.filter(i => {
                  const matchesCategory = selectedCategory === 'all' || i.segment === selectedCategory
                  const matchesSearch = !integratorSearch ||
                    i.name.toLowerCase().includes(integratorSearch.toLowerCase()) ||
                    i.slug.toLowerCase().includes(integratorSearch.toLowerCase())
                  return matchesCategory && matchesSearch
                }).length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No integrators found
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer - always visible */}
          <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between flex-shrink-0">
            <div className="text-sm text-muted-foreground">
              {selectedIntegrator ? (
                <>Selected: <span className="font-medium text-foreground">{integrators.find(i => i.slug === selectedIntegrator)?.name}</span></>
              ) : (
                'Select an integrator to continue'
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowNewDialog(false)}
                disabled={creatingIntegration}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateIntegration}
                disabled={!selectedIntegrator || creatingIntegration}
              >
                {creatingIntegration ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Integration'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Integration Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Integration</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{integrationToDelete?.name}"? This action cannot be undone and will permanently remove all associated data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false)
                setIntegrationToDelete(null)
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  )
}